import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Divider, Flex, Modal, Pagination, Spin, Typography } from "antd";
import { DownloadOutlined, FilterFilled, PaperClipOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { BackButton } from "../../components/Common/BackButton/BackButton";
import { Paths } from "../../models/Paths";
import { AuthService } from "../../services/AuthService";
import { Communication, CommunicationDetail } from "../../models/Communications";
import { CommunicationsFilterPanel } from "./CommunicationsFilterPanel";
import { CommunicationHttpService } from "../../services/Http/CommunicationHttpService";
import { CommunicationBodyRenderer } from "./CommunicationBodyRenderer";
import "./css/CommunicationsPage.css";

const { Text } = Typography;
const PAGE_SIZE = 6;

export function CommunicationsPage() {
    const navigate = useNavigate();
    const isManager = AuthService.isAreaManager();

    // ── List state ────────────────────────────────────────────────────────────
    const [items, setItems] = useState<Communication[]>([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);

    // ── Filters ───────────────────────────────────────────────────────────────
    const [filterVisible, setFilterVisible] = useState(false);
    const [appliedSearch, setAppliedSearch] = useState("");
    const [appliedCategory, setAppliedCategory] = useState<string | undefined>(undefined);

    // ── Detail modal ──────────────────────────────────────────────────────────
    const [detail, setDetail] = useState<CommunicationDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // ── Fetch list ────────────────────────────────────────────────────────────
    const fetchList = useCallback(
        async (page: number, search: string, category: string | undefined) => {
            setLoading(true);
            try {
                const res = await CommunicationHttpService.list(
                    page - 1, // backend is 0-indexed
                    PAGE_SIZE,
                    search || undefined,
                    category,
                );
                setItems(res.data.items);
                setTotal(res.data.allItemsNumber);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    useEffect(() => {
        fetchList(currentPage, appliedSearch, appliedCategory);
    }, [currentPage, appliedSearch, appliedCategory, fetchList]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    function handleApplyFilter(search: string, category: string | undefined) {
        setAppliedSearch(search);
        setAppliedCategory(category);
        setCurrentPage(1);
    }

    function handlePageChange(page: number) {
        setCurrentPage(page);
    }

    function handleNewMessage() {
        navigate(Paths.COMMUNICATIONS_FORM);
    }

    function handleEdit(item: Communication, e: React.MouseEvent) {
        e.stopPropagation();
        navigate(Paths.COMMUNICATIONS_FORM, { state: { item } });
    }

    async function handleRowClick(item: Communication) {
        setDetail(null);
        setDetailLoading(true);
        try {
            const res = await CommunicationHttpService.getById(item.id);
            setDetail(res.data);
        } finally {
            setDetailLoading(false);
        }
    }

    function handleDownload(item: Communication, e: React.MouseEvent) {
        e.stopPropagation();
        // attachmentId is not carried in the list DTO; navigate to detail first
        // then the modal download button handles it
        handleRowClick(item);
    }

    return (
        <div className="communications-page">
            {/* ── Page header ──────────────────────────────────────── */}
            <Flex className="page-title-container" justify="space-between" align="center">
                <Flex align="center" gap="small">
                    <BackButton navigateTo={Paths.HOME} />
                    <Text className="title" strong>
                        News e Comunicazioni
                    </Text>
                </Flex>
                <Button
                    type="text"
                    className={`comm-filter-btn${filterVisible ? " comm-filter-btn--active" : ""}`}
                    icon={<FilterFilled style={{ fontSize: 20 }} />}
                    onClick={() => setFilterVisible((v) => !v)}
                />
            </Flex>

            {/* ── Inline filter panel ───────────────────────────────── */}
            {filterVisible && <CommunicationsFilterPanel onApply={handleApplyFilter} />}

            {/* ── Manager: new message button ─────────────────────────── */}
            {isManager && (
                <Flex justify="flex-end" className="comm-new-bar">
                    <Button type="primary" onClick={handleNewMessage}>
                        Nuovo messaggio
                    </Button>
                </Flex>
            )}

            {/* ── Communications list ───────────────────────────────── */}
            <Spin spinning={loading}>
                <div className="comm-list-container">
                    {items.map((item, idx) => (
                        <React.Fragment key={item.id}>
                            <div className="comm-row comm-row--clickable" onClick={() => handleRowClick(item)}>
                                <div className="comm-info">
                                    <Text strong className="comm-title">
                                        {item.title}
                                    </Text>
                                    <Text className="comm-category">{item.category}</Text>
                                </div>
                                <Flex align="center" gap="middle" className="comm-meta">
                                    <Text className="comm-datetime">
                                        {item.lastUpdateDate}&nbsp;&nbsp;{item.lastUpdateTime}
                                    </Text>
                                    {item.hasAttachment && (
                                        <PaperClipOutlined className="comm-clip-icon" />
                                    )}
                                    {isManager && (
                                        <Button
                                            type="primary"
                                            size="small"
                                            onClick={(e) => handleEdit(item, e)}
                                        >
                                            Modifica
                                        </Button>
                                    )}
                                </Flex>
                            </div>
                            {idx < items.length - 1 && <Divider className="comm-row-divider" />}
                        </React.Fragment>
                    ))}
                    {!loading && items.length === 0 && (
                        <div className="comm-empty">Nessuna comunicazione trovata.</div>
                    )}
                </div>
            </Spin>

            {/* ── Pagination ────────────────────────────────────────── */}
            <Flex justify="center" className="comm-pagination">
                <Pagination
                    current={currentPage}
                    pageSize={PAGE_SIZE}
                    total={total}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                />
            </Flex>

            {/* ── Detail modal ──────────────────────────────────────── */}
            <Modal
                open={detail !== null || detailLoading}
                onCancel={() => { setDetail(null); setDetailLoading(false); }}
                footer={null}
                centered
                width={900}
                title={detail?.title ?? ""}
            >
                {detailLoading && <Flex justify="center" style={{ padding: 32 }}><Spin /></Flex>}
                {detail && (
                    <div className="comm-detail">
                        <Flex justify="space-between" align="center" className="comm-detail-meta">
                            <Text type="secondary">{detail.category}</Text>
                            <Text type="secondary">
                                {detail.lastUpdateDate}&nbsp;&nbsp;{detail.lastUpdateTime}
                            </Text>
                        </Flex>
                        <Divider style={{ margin: "12px 0" }} />
                        <div className="comm-detail-body">
                            <CommunicationBodyRenderer body={detail.body} communicationId={detail.id} />
                        </div>
                        {detail.attachments?.length > 0 && (
                            <Flex gap="small" wrap="wrap" style={{ marginTop: 16 }}>
                                {detail.attachments.map((att) => (
                                    <Button
                                        key={att.id}
                                        icon={<DownloadOutlined />}
                                        onClick={() =>
                                            CommunicationHttpService.downloadAttachment(
                                                detail.id,
                                                att.id,
                                                att.fileName,
                                            )
                                        }
                                    >
                                        {att.fileName}
                                    </Button>
                                ))}
                            </Flex>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
}
